import { MATERIAL_CATEGORIES } from "..";
import { AlertTriangle, Plus } from "lucide-react";

const InwardFormSection = ({ ctx }) => {
  const {
    closeDialog,
    editingInward,
    handleInwardSubmit,
    inwardForm,
    inwardRecords,
    materials,
    pastSuppliers,
    requirements,
    setActiveTab,
    setInwardForm
  } = ctx;
  return (
    <>
            <form onSubmit={handleInwardSubmit} className="space-y-4 mt-4">
              {/* Category Selection */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Category *</label>
                <select
                  required
                  value={inwardForm.category}
                  onChange={(e) => setInwardForm({ ...inwardForm, category: e.target.value, material_id: "", material_requirement_id: "" })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {MATERIAL_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* For Construction Materials - require requirements */}
              {inwardForm.category === "construction" && materials.filter(m => m.category === "construction" && requirements.some(r => r.material_id === m.id)).length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No Material Requirements Found</p>
                  <p className="text-sm text-slate-500 mt-2">Please add material requirements first before recording inward entries for construction materials.</p>
                  <button type="button" onClick={() => { closeDialog(); setActiveTab("requirements"); }} className="btn-primary mt-4">
                    Go to Requirements
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Material *</label>
                      {inwardForm.category !== "construction" && !editingInward && (
                        <div className="flex mb-2 border border-slate-300 rounded-sm overflow-hidden text-xs font-medium">
                          <button type="button" data-testid="inward-existing-material-btn"
                            onClick={() => setInwardForm({ ...inwardForm, is_new_material: false })}
                            className={`flex-1 py-1.5 ${!inwardForm.is_new_material ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                            Existing Item
                          </button>
                          <button type="button" data-testid="inward-new-material-btn"
                            onClick={() => setInwardForm({ ...inwardForm, is_new_material: true, material_id: "", new_material_unit: inwardForm.new_material_unit || "Nos" })}
                            className={`flex-1 py-1.5 flex items-center justify-center gap-1 ${inwardForm.is_new_material ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                            <Plus className="w-3 h-3" /> New Material
                          </button>
                        </div>
                      )}
                      {inwardForm.is_new_material && inwardForm.category !== "construction" ? (
                        <div className="grid grid-cols-3 gap-2">
                          <input type="text" required placeholder="Material name (e.g. Hydraulic Filter)"
                            data-testid="inward-new-material-name"
                            value={inwardForm.new_material_name || ""}
                            onChange={(e) => setInwardForm({ ...inwardForm, new_material_name: e.target.value })}
                            className="col-span-2 w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
                          <select value={inwardForm.new_material_unit || "Nos"} data-testid="inward-new-material-unit"
                            onChange={(e) => setInwardForm({ ...inwardForm, new_material_unit: e.target.value })}
                            className="w-full px-2 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm">
                            {["Nos", "Pcs", "Set", "Kg", "Liters", "MT", "Bags", "Cum", "Mtr", "Box", "Pair"].map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      ) : (
                      <select
                        required
                        data-testid="inward-material-select"
                        value={inwardForm.material_id}
                        onChange={(e) => {
                          const matId = e.target.value;
                          const linkedReq = requirements.find(r => r.material_id === matId);
                          const selectedMat = materials.find(m => m.id === matId);
                          setInwardForm({ 
                            ...inwardForm, 
                            material_id: matId,
                            material_requirement_id: linkedReq?.id || "",
                            entered_rate: selectedMat?.rate || ""  // Pre-fill with Stock Register rate
                          });
                        }}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select Material</option>
                        {inwardForm.category === "construction" 
                          ? materials.filter(m => m.category === "construction" && requirements.some(r => r.material_id === m.id)).map((m) => {
                              const req = requirements.find(r => r.material_id === m.id);
                              const totalInward = inwardRecords
                                .filter(ir => ir.material_requirement_id === req?.id)
                                .reduce((sum, ir) => sum + (ir.quantity || 0), 0);
                              const remaining = req ? req.planned_quantity - totalInward : 0;
                              return (
                                <option key={m.id} value={m.id}>
                                  {m.name} (Remaining: {remaining.toFixed(2)} {m.unit}) - Rate: ₹{m.rate || 0}
                                </option>
                              );
                            })
                          : materials.filter(m => m.category === inwardForm.category).map((m) => (
                              <option key={m.id} value={m.id}>{m.name} ({m.unit}) - Rate: ₹{m.rate || 0}</option>
                            ))
                        }
                      </select>
                      )}
                    </div>
                    {inwardForm.category === "construction" && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Linked Requirement</label>
                      <input
                        type="text"
                        readOnly
                        value={(() => {
                          const req = requirements.find(r => r.material_id === inwardForm.material_id);
                          if (!req) return "No requirement linked";
                          const totalInward = inwardRecords
                            .filter(ir => ir.material_requirement_id === req.id && ir.status !== "Rejected")
                            .reduce((sum, ir) => sum + (ir.quantity || 0), 0);
                          const isLumpsum = req.is_lumpsum ? " [LUMPSUM]" : "";
                          const mgmtRate = req.management_rate ? ` | Rate: ₹${req.management_rate}` : " | Rate: Not Set";
                          return `Planned: ${req.planned_quantity}${isLumpsum} | Received: ${totalInward.toFixed(2)}${mgmtRate}`;
                        })()}
                        className="w-full px-4 py-2 border border-slate-200 rounded-sm bg-slate-50 text-slate-600 font-mono text-sm"
                      />
                    </div>
                    )}
                    {inwardForm.category !== "construction" && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                      <input
                        type="date"
                        required
                        value={inwardForm.date}
                        onChange={(e) => setInwardForm({ ...inwardForm, date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    )}
                  </div>
                  
                  {/* Show rate and quantity rules - only for construction materials */}
                  {inwardForm.category === "construction" && inwardForm.material_id && (
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-2">
                      {(() => {
                        const req = requirements.find(r => r.material_id === inwardForm.material_id);
                        if (!req) return null;
                        const totalInward = inwardRecords
                          .filter(ir => ir.material_requirement_id === req.id && ir.status !== "Rejected")
                          .reduce((sum, ir) => sum + (ir.quantity || 0), 0);
                        const remaining = req.planned_quantity - totalInward;
                        
                        return (
                          <>
                            {req.is_lumpsum ? (
                              <p className="text-sm text-blue-800">
                                <strong>Lumpsum Item:</strong> Quantity is flexible. No quantity restriction applies.
                              </p>
                            ) : (
                              <p className="text-sm text-blue-800">
                                <strong>Quantity Limit:</strong> Maximum allowed: {remaining.toFixed(2)}. Exceeding requires approval.
                              </p>
                            )}
                            {req.management_rate > 0 ? (
                              <p className="text-sm text-blue-800">
                                <strong>Rate:</strong> Management rate: ₹{req.management_rate}. Variance up to ±{req.rate_variance_allowed || 3}% allowed. Beyond requires approval.
                              </p>
                            ) : (
                              <p className="text-sm text-blue-800">
                                <strong>Rate:</strong> No management rate set. You can enter any rate.
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date</label>
                      <input
                        type="date"
                        required
                        value={inwardForm.date}
                        onChange={(e) => setInwardForm({ ...inwardForm, date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Entered Rate (₹) *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={inwardForm.entered_rate}
                        onChange={(e) => setInwardForm({ ...inwardForm, entered_rate: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="Rate per unit"
                      />
                      {inwardForm.material_id && (() => {
                        const selectedMaterial = materials.find(m => m.id === inwardForm.material_id);
                        const stockRate = selectedMaterial?.rate || 0;
                        if (stockRate > 0) {
                          const minRate = (stockRate * 0.97).toFixed(2);
                          const maxRate = (stockRate * 1.03).toFixed(2);
                          return (
                            <p className="text-xs text-slate-500 mt-1">
                              Stock Register Rate: ₹{stockRate.toFixed(2)} | Allowed: ₹{minRate} - ₹{maxRate} (±3%)
                            </p>
                          );
                        }
                        return <p className="text-xs text-amber-600 mt-1">No rate set in Stock Register - any rate will be accepted</p>;
                      })()}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        min="0.01"
                        value={inwardForm.quantity}
                        onChange={(e) => setInwardForm({ ...inwardForm, quantity: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="Enter quantity"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Challan Number</label>
                      <input
                        type="text"
                        required
                        value={inwardForm.challan_number}
                        onChange={(e) => setInwardForm({ ...inwardForm, challan_number: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Supplier Name</label>
                      <input
                        type="text"
                        required
                        value={inwardForm.supplier_name}
                        onChange={(e) => setInwardForm({ ...inwardForm, supplier_name: e.target.value })}
                        list="past-suppliers"
                        autoComplete="off"
                        data-testid="inward-supplier-input"
                        placeholder="Type or pick a past supplier"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <datalist id="past-suppliers">
                        {pastSuppliers.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Vehicle Number</label>
                      <input
                        type="text"
                        required
                        value={inwardForm.vehicle_number}
                        onChange={(e) => setInwardForm({ ...inwardForm, vehicle_number: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Received By</label>
                      <input
                        type="text"
                        required
                        value={inwardForm.received_by}
                        onChange={(e) => setInwardForm({ ...inwardForm, received_by: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quality Status</label>
                      <select
                        value={inwardForm.quality_status}
                        onChange={(e) => setInwardForm({ ...inwardForm, quality_status: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Approved">Approved</option>
                        <option value="Pending">Pending Inspection</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                    <textarea
                      value={inwardForm.remarks}
                      onChange={(e) => setInwardForm({ ...inwardForm, remarks: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="2"
                    />
                  </div>
                  
                  {/* Bill/Invoice Section */}
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 space-y-3">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="bill_received"
                        checked={inwardForm.bill_received}
                        onChange={(e) => setInwardForm({ ...inwardForm, bill_received: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <label htmlFor="bill_received" className="text-sm font-medium text-amber-800">
                        Bill / Invoice Received
                      </label>
                    </div>
                    {inwardForm.bill_received && (
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2">Bill/Invoice Number</label>
                          <input
                            type="text"
                            value={inwardForm.bill_number}
                            onChange={(e) => setInwardForm({ ...inwardForm, bill_number: e.target.value })}
                            placeholder="INV-001"
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2">Bill/Invoice Date</label>
                          <input
                            type="date"
                            value={inwardForm.bill_date}
                            onChange={(e) => setInwardForm({ ...inwardForm, bill_date: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                    <button type="submit" className="btn-primary">{editingInward ? 'Update' : 'Record'} Inward</button>
                  </div>
                </>
              )}
            </form>
    </>
  );
};

export default InwardFormSection;
